import { z } from 'zod';
import * as C from './constants';

/**
 * NFR-9 — "config … lives in one validated module".
 *
 * This runs at import time. `./index.ts` imports it, and every consumer imports `./index.ts`, so
 * an out-of-range constant throws before the first screen renders rather than producing a subtly
 * wrong measurement three hours later at a match.
 *
 * The schema encodes relationships, not just types. `HARNESS_TRIALS_DEFAULT` sitting outside
 * `[MIN, MAX]` is the kind of edit that looks harmless in a diff and silently disables the
 * verdict's sample-size guard.
 */

const positiveInt = z.number().int().positive();
const nonNegativeInt = z.number().int().nonnegative();
const fraction = z.number().gt(0).lt(1);

const ConfigSchema = z
  .object({
    // §1.3
    PATTERN_THRESHOLD: positiveInt,
    BOARD_CAP: positiveInt,
    TALKPOINT_COUNT: positiveInt,
    VOICE_NOTE_MAX_S: positiveInt,
    UNDO_WINDOW_S: positiveInt,
    LOCK_NUDGE_MIN: positiveInt,
    HALF_OVERRUN_NUDGE_MIN: positiveInt,
    REORDER_QUIET_MS: positiveInt,
    TRANSITION_HOLD_MS: positiveInt,
    HALF_LENGTH_DEFAULT: positiveInt,
    HALF_LENGTH_MIN_MIN: positiveInt,
    HALF_LENGTH_MIN_MAX: positiveInt,
    ACK_COMMIT_BUDGET_MS: positiveInt,
    TIMESLICE_MS: positiveInt,
    HT_ADJUSTMENT_CANDIDATES: positiveInt,
    LONG_PRESS_MS: positiveInt,
    TAP_SLOP_PX: positiveInt,
    SWIPE_MIN_PX: positiveInt,
    COLD_LAUNCH_BUDGET_MS: positiveInt,
    MIN_TOUCH_TARGET_PT: positiveInt,
    FLAG_BAR_MIN_PT: positiveInt,
    STORAGE_BUDGET_3MATCH_MB: positiveInt,
    MIN_SEEDS_PER_BUCKET: positiveInt,
    DEBUG_TAP_COUNT: positiveInt,
    ACK_FLASH_SCALE: positiveInt,
    TAP_ACK_BUDGET_MS: positiveInt,
    ACK_PERSIST_MIN_MS: positiveInt,
    HOLD_SLOP_MS: positiveInt,
    REORDER_ANIM_MS: positiveInt,
    UNDO_INSET_PT: positiveInt,
    ADJUSTMENT_TEXT_PT: positiveInt,
    MANIFEST_TIMEOUT_MS: positiveInt,
    ARCHIVE_PUT_TIMEOUT_MS: positiveInt,
    MAX_ARCHIVE_MB: positiveInt,
    PLAYABILITY_PROBE_TIMEOUT_MS: positiveInt,

    // U-0
    LOG_RING_CAP: positiveInt,
    LOG_FLUSH_THRESHOLD: positiveInt,
    LOG_TRIM_EVERY_N_FLUSHES: positiveInt,
    A1_ASSUMED_WARM_P95_MS: positiveInt,
    HARNESS_WARMUP_TRIALS: nonNegativeInt,
    HARNESS_TRIALS_DEFAULT: positiveInt,
    HARNESS_TRIALS_MIN: positiveInt,
    HARNESS_TRIALS_MAX: positiveInt,
    HARNESS_P99_MIN_SAMPLES: positiveInt,
    HARNESS_LIVE_TAP_MAX: positiveInt,
    HARNESS_COMMIT_EVENT_TIMEOUT_MS: positiveInt,
    HARNESS_FRAME_TIMEOUT_MS: positiveInt,
    HARNESS_PACING_REALISTIC_MS: positiveInt,
    HARNESS_THERMAL_SOAK_S: nonNegativeInt,
    HARNESS_THERMAL_SOAK_MAX_S: positiveInt,
    HARNESS_HEAT_WORKERS_MAX: positiveInt,
    SEED_MATCHES_DEFAULT: nonNegativeInt,
    SEED_OBSERVATIONS_PER_MATCH_DEFAULT: nonNegativeInt,
    SEED_HYPOTHESES_PER_MATCH: positiveInt,
    SEED_CHUNK_ROWS: positiveInt,
    SEED_AUDIO_BLOB_BYTES: positiveInt,
    STORAGE_WARN_FRACTION: fraction,
    HARNESS_RUN_HISTORY_CAP: positiveInt,

    // U-1
    AUDIO_MIME_CANDIDATES: z.array(z.string().min(1)).min(1),
    AUDIO_SPIKE_SEGMENT_S: positiveInt,
    AUDIO_SPIKE_SEGMENT_MIN_S: positiveInt,
    AUDIO_SPIKE_MIN_CHUNKS: positiveInt,
    AUDIO_SPIKE_MIN_SEGMENTS: positiveInt,
    GUM_TRIALS_DEFAULT: positiveInt,
    GUM_TRIALS_MIN: positiveInt,
    GUM_TRIALS_MAX: positiveInt,
    GUM_COLD_ASSUMED_MAX_MS: positiveInt,
    TIMESLICE_TOLERANCE_FRACTION: fraction,
    TIMESLICE_MAX_INTERVAL_FACTOR: z.number().gt(1),
    AUDIO_RUN_HISTORY_CAP: positiveInt,
    AUDIO_SEGMENT_HISTORY_CAP: positiveInt,
    AUDIO_SPIKE_DEFAULT_MATCH_MINUTE: nonNegativeInt,
    AUDIO_SPIKE_MAX_MATCH_MINUTE: positiveInt,

    // U-3
    COMMIT_EVENT_TIMEOUT_MS: positiveInt,
    GEOMETRY_DECIMALS: positiveInt,

    // U-5
    ARCHIVE_FORMAT_VERSION: positiveInt,

    HARNESS_DB_NAME: z.string().min(1),
    HARNESS_DB_VERSION: positiveInt,
    PRODUCT_DB_NAME: z.string().min(1),
    PRODUCT_DB_VERSION: positiveInt,

    // U-7
    SHIRT_NUMBER_MIN: positiveInt,
    SHIRT_NUMBER_MAX: positiveInt,

    // U-11
    RECORDER_STOP_TIMEOUT_MS: positiveInt,

    // U-12
    SET_PIECE_MARKER_PT: positiveInt,
    SET_PIECE_DIAGRAM_ASPECT: z.number().positive().finite(),

    // U-15
    CARD_WIDTH_PX: positiveInt,
    CARD_TALK_POINT_PX: positiveInt,
    CARD_MESSAGE_WIDTH_PT: positiveInt,
    CARD_MIN_LEGIBLE_PT: positiveInt,

    // U-20
    LOG_FLUSH_IDLE_MS: positiveInt,

    // B-3
    COACH_TIMEOUT_MS: positiveInt,
    COACH_PREFLIGHT_TIMEOUT_MS: positiveInt,
    COACH_MAX_SUGGESTIONS: positiveInt,
    COACH_ADJUSTMENT_MAX_CHARS: positiveInt,
    COACH_CARD_MAX_ENTRIES: positiveInt,
    COACH_REQUEST_MAX_KB: positiveInt,

    // B-19a
    LOCK_NUDGE_MIN_MIN: positiveInt,
    LOCK_NUDGE_MIN_MAX: positiveInt,
    LOCK_NUDGE_STEP_MIN: positiveInt,
  })
  .superRefine((c, ctx) => {
    const relation = (ok: boolean, message: string) => {
      if (!ok) ctx.addIssue({ code: z.ZodIssueCode.custom, message });
    };

    relation(
      c.HALF_LENGTH_MIN_MIN < c.HALF_LENGTH_MIN_MAX,
      'SES-5: HALF_LENGTH_MIN_MIN must be below HALF_LENGTH_MIN_MAX',
    );
    relation(
      c.HALF_LENGTH_DEFAULT >= c.HALF_LENGTH_MIN_MIN &&
        c.HALF_LENGTH_DEFAULT <= c.HALF_LENGTH_MIN_MAX,
      'SES-5: HALF_LENGTH_DEFAULT must lie inside [HALF_LENGTH_MIN_MIN, HALF_LENGTH_MIN_MAX]',
    );
    relation(
      c.ACK_COMMIT_BUDGET_MS < c.TAP_ACK_BUDGET_MS,
      'Design §5.3: ACK_COMMIT_BUDGET_MS is the commit slice of NFR-1’s budget and must be strictly below it',
    );
    relation(
      c.A1_ASSUMED_WARM_P95_MS < c.ACK_COMMIT_BUDGET_MS,
      'Design §0.1: A-1’s assumed warm p95 must sit inside ACK_COMMIT_BUDGET_MS, or the design’s own budget arithmetic does not close',
    );
    relation(
      c.HARNESS_TRIALS_MIN <= c.HARNESS_TRIALS_DEFAULT &&
        c.HARNESS_TRIALS_DEFAULT <= c.HARNESS_TRIALS_MAX,
      'U-0: HARNESS_TRIALS_DEFAULT must lie inside [HARNESS_TRIALS_MIN, HARNESS_TRIALS_MAX]',
    );
    relation(
      c.HARNESS_TRIALS_MIN <= c.HARNESS_LIVE_TAP_MAX,
      'U-0: HARNESS_LIVE_TAP_MAX must be at least HARNESS_TRIALS_MIN, or the live-tap pad stops accepting taps before it can ever reach an admissible sample and the one measurement carrying real touch input can never produce a verdict',
    );
    relation(
      c.HARNESS_THERMAL_SOAK_S <= c.HARNESS_THERMAL_SOAK_MAX_S,
      'U-0: HARNESS_THERMAL_SOAK_S must not exceed HARNESS_THERMAL_SOAK_MAX_S',
    );
    relation(
      c.LOG_FLUSH_THRESHOLD < c.LOG_RING_CAP,
      'OBS-1: a flush threshold at or above the ring cap would trim the buffer on every flush',
    );
    relation(
      c.PATTERN_THRESHOLD <= c.BOARD_CAP,
      'INV-3/INV-5: a threshold above BOARD_CAP would make a confirmed board unreachable',
    );
    // ── U-2 ──────────────────────────────────────────────────────────────────
    relation(
      c.TRANSITION_HOLD_MS > c.LONG_PRESS_MS,
      'Design §2.2 (SES-1): TRANSITION_HOLD_MS must sit clearly above LONG_PRESS_MS, or the two sustained-touch gestures — a confirming hold and a voice-note long-press — stop being distinguishable',
    );
    relation(
      c.BOARD_CAP <= c.HT_ADJUSTMENT_CANDIDATES,
      'HT-5 / design §7.3: the halftime candidate list must be able to show a full board, or a locked-in hypothesis becomes unpromotable at the break',
    );

    // ── U-1 ──────────────────────────────────────────────────────────────────
    relation(
      c.GUM_TRIALS_MIN <= c.GUM_TRIALS_DEFAULT && c.GUM_TRIALS_DEFAULT <= c.GUM_TRIALS_MAX,
      'U-1: GUM_TRIALS_DEFAULT must lie inside [GUM_TRIALS_MIN, GUM_TRIALS_MAX]',
    );
    relation(
      c.AUDIO_SPIKE_SEGMENT_MIN_S <= c.AUDIO_SPIKE_SEGMENT_S,
      'U-1: AUDIO_SPIKE_SEGMENT_MIN_S must not exceed the default segment length',
    );
    relation(
      c.AUDIO_SPIKE_SEGMENT_S <= c.VOICE_NOTE_MAX_S,
      'AUD-2: a spike segment cannot outlast VOICE_NOTE_MAX_S — the auto-stop would fire first and the segment would not be the length it claims',
    );
    relation(
      c.AUDIO_SPIKE_MIN_CHUNKS * c.TIMESLICE_MS <= c.AUDIO_SPIKE_SEGMENT_MIN_S * 1000,
      'U-1: the shortest permitted segment must still be able to produce AUDIO_SPIKE_MIN_CHUNKS timeslices, or every segment is excluded from the A-2 verdict by construction',
    );
    relation(
      c.AUDIO_SPIKE_DEFAULT_MATCH_MINUTE <= c.AUDIO_SPIKE_MAX_MATCH_MINUTE,
      'U-1: the default pretend match minute must lie inside the input’s own bound',
    );
    // ── U-7 ──────────────────────────────────────────────────────────────────
    relation(
      c.SHIRT_NUMBER_MIN < c.SHIRT_NUMBER_MAX,
      'PRE-4: SHIRT_NUMBER_MIN must be below SHIRT_NUMBER_MAX, or the number wheel has no positions and the stub flow cannot be completed by taps alone',
    );

    relation(
      c.PLAYABILITY_PROBE_TIMEOUT_MS > c.TAP_ACK_BUDGET_MS,
      'Design §8.2/§13.1: the playability probe is an off-tap-path operation and its bound must sit well above the tap budget, or a slow decode reads as an unplayable segment',
    );

    // ── U-3 ──────────────────────────────────────────────────────────────────
    relation(
      c.COMMIT_EVENT_TIMEOUT_MS > c.TAP_ACK_BUDGET_MS,
      'SES-2/design §5.1: the commit stall detector must sit far above NFR-1’s tap budget — set at or below it, a merely slow commit would be reported as a failed one and the user would be told a committed tap was lost',
    );
    // ── U-5 ──────────────────────────────────────────────────────────────────
    relation(
      c.GEOMETRY_DECIMALS <= 15,
      'INV-12/§11.7: geometry cannot be rounded to more decimals than a double can represent exactly, or the "no floats beyond N dp" control would round to a value that varies with the input’s binary representation',
    );

    // ── U-11 ─────────────────────────────────────────────────────────────────
    relation(
      c.RECORDER_STOP_TIMEOUT_MS > c.TIMESLICE_MS,
      'AUD-3/§8.5: the stop-event bound must exceed one timeslice, or finalize would abandon the trailing dataavailable that `stop()` is about to emit and throw away audio that was on its way to disk',
    );

    // ── U-12 ─────────────────────────────────────────────────────────────────
    relation(
      c.SET_PIECE_MARKER_PT <= c.MIN_TOUCH_TARGET_PT,
      'SP-2/NFR-3: a placed marker is a target nested inside the diagram, so it must not be wider than a primary in-play target — at or above MIN_TOUCH_TARGET_PT it swallows the tap aimed at the space beside it and a line of defenders across the six-yard box becomes uncapturable',
    );

    // ── U-15 ─────────────────────────────────────────────────────────────────
    //
    // CARD-1's *"legible at phone-message size"*, stated as a relationship because it is one. The
    // card's type size means nothing on its own: a message client scales the PNG to the bubble's
    // width, so what reaches the reader's eye is `type ÷ card width × bubble width`. Encoding it
    // here is what makes the requirement fail the BUILD rather than fail on the pitch — every other
    // check in this repo would stay green on a card whose text renders at 6 pt.
    relation(
      (c.CARD_TALK_POINT_PX * c.CARD_MESSAGE_WIDTH_PT) / c.CARD_WIDTH_PX >= c.CARD_MIN_LEGIBLE_PT,
      `CARD-1: a talk point must still be legible once a message client scales the card into a bubble — ${String(
        c.CARD_TALK_POINT_PX,
      )}px of type on a ${String(c.CARD_WIDTH_PX)}px card renders at ${(
        (c.CARD_TALK_POINT_PX * c.CARD_MESSAGE_WIDTH_PT) /
        c.CARD_WIDTH_PX
      ).toFixed(1)}pt in a ${String(c.CARD_MESSAGE_WIDTH_PT)}pt bubble, below the ${String(
        c.CARD_MIN_LEGIBLE_PT,
      )}pt floor. Widen the type or narrow the card`,
    );

    relation(
      c.CARD_TALK_POINT_PX < c.CARD_WIDTH_PX,
      'CARD-1: a single glyph cannot be wider than the card it is drawn on — no wrap would terminate',
    );

    // ── U-20 ─────────────────────────────────────────────────────────────────
    //
    // §12.1a's two OBS-1 flush bounds are a *pair*, and the pair only works if the time bound is
    // slower than the burst bound. If a deferral could fire faster than a burst fills the buffer,
    // the deferred flush would become the primary trigger and reintroduce the per-entry write rate
    // §12.1 wrote the buffer to avoid — the exact failure §12.1a rejects the `requestIdleCallback`
    // shim for. Stated as the relationship rather than as a comment on either constant, because
    // neither value is wrong on its own and only their ratio can be.
    relation(
      c.LOG_FLUSH_IDLE_MS > c.ACK_COMMIT_BUDGET_MS,
      'OBS-1 / §12.1a: LOG_FLUSH_IDLE_MS must sit above the acknowledgment budget, or a deferred flush could land inside the tap→ack window it is required to stay out of',
    );

    relation(
      c.PRODUCT_DB_NAME !== c.HARNESS_DB_NAME,
      'NFR-7: the product schema must start at its own v001 on its own database — sharing the harness database name would put real scouting data in the store the harness wipes',
    );

    // ── B-3 (B spec §4.3) ────────────────────────────────────────────────────
    relation(
      c.COACH_PREFLIGHT_TIMEOUT_MS < c.COACH_TIMEOUT_MS,
      'B §4.3 / §2.6: the pre-flight must answer well before a generate would — a health check that waits as long as the model call cannot catch the config failure it exists to catch before match day',
    );
    relation(
      c.COACH_MAX_SUGGESTIONS <= c.COACH_CARD_MAX_ENTRIES,
      'B §4.3: one generate response committed into an EMPTY draft must fit inside the card — with more permitted suggestions than card entries, the AI path would overflow ERR_CARD_TOO_LARGE on its own output',
    );
    relation(
      c.COACH_ADJUSTMENT_MAX_CHARS * c.COACH_CARD_MAX_ENTRIES <= c.COACH_REQUEST_MAX_KB * 1024,
      'B §4.3: a full card’s text (chars × entries) must stay bounded under the request cap’s order of magnitude — the adjustments JSON column is read whole at interaction grain, and an unbounded product would let a single draft row outgrow the small-transaction shape §2.5’s no-debounce commits depend on',
    );

    // ── B-19a — the band a user-set `lockNudgeMin` may occupy ────────────────
    relation(
      c.LOCK_NUDGE_MIN_MIN < c.LOCK_NUDGE_MIN_MAX,
      'LOCK-1 / BAK-5: LOCK_NUDGE_MIN_MIN must be below LOCK_NUDGE_MIN_MAX, or the setting offers no choice at all',
    );
    relation(
      c.LOCK_NUDGE_MIN >= c.LOCK_NUDGE_MIN_MIN && c.LOCK_NUDGE_MIN <= c.LOCK_NUDGE_MIN_MAX,
      'LOCK-1: LOCK_NUDGE_MIN is the default the parse falls back to, so it must itself lie inside [LOCK_NUDGE_MIN_MIN, LOCK_NUDGE_MIN_MAX] — a default outside the band would be rejected by its own validator',
    );
    /**
     * The derivation `LOCK_NUDGE_MIN_MAX = HALF_LENGTH_MIN_MIN` stated as a relation, so the two
     * cannot be decoupled by an edit. At halftime the derived minute IS `halfLengthMin`, so a
     * threshold above the shortest permitted half would put LOCK-1's offer — a MUST — beyond the
     * only break the match has, on a legal `Match.halfLengthMin`.
     */
    relation(
      c.LOCK_NUDGE_MIN_MAX <= c.HALF_LENGTH_MIN_MIN,
      'LOCK-1 / SES-5: LOCK_NUDGE_MIN_MAX must not exceed HALF_LENGTH_MIN_MIN — at halftime the derived minute is halfLengthMin, so a threshold above the shortest permitted half would leave the "Lock my 8" offer unreachable before the break on a legal match configuration',
    );
    relation(
      (c.LOCK_NUDGE_MIN - c.LOCK_NUDGE_MIN_MIN) % c.LOCK_NUDGE_STEP_MIN === 0,
      'LOCK-1: LOCK_NUDGE_MIN must land on a LOCK_NUDGE_STEP_MIN step from LOCK_NUDGE_MIN_MIN, or B’s More tab derives a choice list that does not contain the default and a fresh install renders the control with nothing selected',
    );
    relation(
      c.LOCK_NUDGE_STEP_MIN <= c.LOCK_NUDGE_MIN_MAX - c.LOCK_NUDGE_MIN_MIN,
      'LOCK-1: a step wider than the band would derive a single-choice control, which is not a choice',
    );
  });

export type TouchlineConfig = z.infer<typeof ConfigSchema>;

/** Thrown at import time. Deliberately not an `Error` subclass consumers are expected to catch. */
export class ConfigValidationError extends Error {
  override readonly name = 'ConfigValidationError';
  constructor(readonly issues: string[]) {
    super(`NFR-9: configuration is invalid.\n  - ${issues.join('\n  - ')}`);
  }
}

/**
 * Exported so the unit tests can drive the unhappy paths without mutating module state.
 * `validateConfig(CONSTANTS)` is what runs at import.
 */
export function validateConfig(candidate: unknown): TouchlineConfig {
  const parsed = ConfigSchema.safeParse(candidate);
  if (!parsed.success) {
    throw new ConfigValidationError(
      parsed.error.issues.map((i) => `${i.path.join('.') || '(config)'}: ${i.message}`),
    );
  }
  return parsed.data;
}

/** The validated, frozen configuration. This — not `constants.ts` — is what call sites read. */
export const config: Readonly<TouchlineConfig> = Object.freeze(
  validateConfig({
    PATTERN_THRESHOLD: C.PATTERN_THRESHOLD,
    BOARD_CAP: C.BOARD_CAP,
    TALKPOINT_COUNT: C.TALKPOINT_COUNT,
    VOICE_NOTE_MAX_S: C.VOICE_NOTE_MAX_S,
    UNDO_WINDOW_S: C.UNDO_WINDOW_S,
    LOCK_NUDGE_MIN: C.LOCK_NUDGE_MIN,
    HALF_OVERRUN_NUDGE_MIN: C.HALF_OVERRUN_NUDGE_MIN,
    REORDER_QUIET_MS: C.REORDER_QUIET_MS,
    TRANSITION_HOLD_MS: C.TRANSITION_HOLD_MS,
    HALF_LENGTH_DEFAULT: C.HALF_LENGTH_DEFAULT,
    HALF_LENGTH_MIN_MIN: C.HALF_LENGTH_MIN_MIN,
    HALF_LENGTH_MIN_MAX: C.HALF_LENGTH_MIN_MAX,
    ACK_COMMIT_BUDGET_MS: C.ACK_COMMIT_BUDGET_MS,
    TIMESLICE_MS: C.TIMESLICE_MS,
    HT_ADJUSTMENT_CANDIDATES: C.HT_ADJUSTMENT_CANDIDATES,
    LONG_PRESS_MS: C.LONG_PRESS_MS,
    TAP_SLOP_PX: C.TAP_SLOP_PX,
    SWIPE_MIN_PX: C.SWIPE_MIN_PX,
    COLD_LAUNCH_BUDGET_MS: C.COLD_LAUNCH_BUDGET_MS,
    MIN_TOUCH_TARGET_PT: C.MIN_TOUCH_TARGET_PT,
    FLAG_BAR_MIN_PT: C.FLAG_BAR_MIN_PT,
    STORAGE_BUDGET_3MATCH_MB: C.STORAGE_BUDGET_3MATCH_MB,
    MIN_SEEDS_PER_BUCKET: C.MIN_SEEDS_PER_BUCKET,
    DEBUG_TAP_COUNT: C.DEBUG_TAP_COUNT,
    ACK_FLASH_SCALE: C.ACK_FLASH_SCALE,
    TAP_ACK_BUDGET_MS: C.TAP_ACK_BUDGET_MS,
    ACK_PERSIST_MIN_MS: C.ACK_PERSIST_MIN_MS,
    HOLD_SLOP_MS: C.HOLD_SLOP_MS,
    REORDER_ANIM_MS: C.REORDER_ANIM_MS,
    UNDO_INSET_PT: C.UNDO_INSET_PT,
    ADJUSTMENT_TEXT_PT: C.ADJUSTMENT_TEXT_PT,
    MANIFEST_TIMEOUT_MS: C.MANIFEST_TIMEOUT_MS,
    ARCHIVE_PUT_TIMEOUT_MS: C.ARCHIVE_PUT_TIMEOUT_MS,
    MAX_ARCHIVE_MB: C.MAX_ARCHIVE_MB,
    PLAYABILITY_PROBE_TIMEOUT_MS: C.PLAYABILITY_PROBE_TIMEOUT_MS,

    LOG_RING_CAP: C.LOG_RING_CAP,
    LOG_FLUSH_THRESHOLD: C.LOG_FLUSH_THRESHOLD,
    LOG_TRIM_EVERY_N_FLUSHES: C.LOG_TRIM_EVERY_N_FLUSHES,
    A1_ASSUMED_WARM_P95_MS: C.A1_ASSUMED_WARM_P95_MS,
    HARNESS_WARMUP_TRIALS: C.HARNESS_WARMUP_TRIALS,
    HARNESS_TRIALS_DEFAULT: C.HARNESS_TRIALS_DEFAULT,
    HARNESS_TRIALS_MIN: C.HARNESS_TRIALS_MIN,
    HARNESS_TRIALS_MAX: C.HARNESS_TRIALS_MAX,
    HARNESS_P99_MIN_SAMPLES: C.HARNESS_P99_MIN_SAMPLES,
    HARNESS_LIVE_TAP_MAX: C.HARNESS_LIVE_TAP_MAX,
    HARNESS_COMMIT_EVENT_TIMEOUT_MS: C.HARNESS_COMMIT_EVENT_TIMEOUT_MS,
    HARNESS_FRAME_TIMEOUT_MS: C.HARNESS_FRAME_TIMEOUT_MS,
    HARNESS_PACING_REALISTIC_MS: C.HARNESS_PACING_REALISTIC_MS,
    HARNESS_THERMAL_SOAK_S: C.HARNESS_THERMAL_SOAK_S,
    HARNESS_THERMAL_SOAK_MAX_S: C.HARNESS_THERMAL_SOAK_MAX_S,
    HARNESS_HEAT_WORKERS_MAX: C.HARNESS_HEAT_WORKERS_MAX,
    SEED_MATCHES_DEFAULT: C.SEED_MATCHES_DEFAULT,
    SEED_OBSERVATIONS_PER_MATCH_DEFAULT: C.SEED_OBSERVATIONS_PER_MATCH_DEFAULT,
    SEED_HYPOTHESES_PER_MATCH: C.SEED_HYPOTHESES_PER_MATCH,
    SEED_CHUNK_ROWS: C.SEED_CHUNK_ROWS,
    SEED_AUDIO_BLOB_BYTES: C.SEED_AUDIO_BLOB_BYTES,
    STORAGE_WARN_FRACTION: C.STORAGE_WARN_FRACTION,
    HARNESS_RUN_HISTORY_CAP: C.HARNESS_RUN_HISTORY_CAP,

    AUDIO_MIME_CANDIDATES: C.AUDIO_MIME_CANDIDATES,
    AUDIO_SPIKE_SEGMENT_S: C.AUDIO_SPIKE_SEGMENT_S,
    AUDIO_SPIKE_SEGMENT_MIN_S: C.AUDIO_SPIKE_SEGMENT_MIN_S,
    AUDIO_SPIKE_MIN_CHUNKS: C.AUDIO_SPIKE_MIN_CHUNKS,
    AUDIO_SPIKE_MIN_SEGMENTS: C.AUDIO_SPIKE_MIN_SEGMENTS,
    GUM_TRIALS_DEFAULT: C.GUM_TRIALS_DEFAULT,
    GUM_TRIALS_MIN: C.GUM_TRIALS_MIN,
    GUM_TRIALS_MAX: C.GUM_TRIALS_MAX,
    GUM_COLD_ASSUMED_MAX_MS: C.GUM_COLD_ASSUMED_MAX_MS,
    TIMESLICE_TOLERANCE_FRACTION: C.TIMESLICE_TOLERANCE_FRACTION,
    TIMESLICE_MAX_INTERVAL_FACTOR: C.TIMESLICE_MAX_INTERVAL_FACTOR,
    AUDIO_RUN_HISTORY_CAP: C.AUDIO_RUN_HISTORY_CAP,
    AUDIO_SEGMENT_HISTORY_CAP: C.AUDIO_SEGMENT_HISTORY_CAP,
    AUDIO_SPIKE_DEFAULT_MATCH_MINUTE: C.AUDIO_SPIKE_DEFAULT_MATCH_MINUTE,
    AUDIO_SPIKE_MAX_MATCH_MINUTE: C.AUDIO_SPIKE_MAX_MATCH_MINUTE,

    COMMIT_EVENT_TIMEOUT_MS: C.COMMIT_EVENT_TIMEOUT_MS,
    GEOMETRY_DECIMALS: C.GEOMETRY_DECIMALS,
    ARCHIVE_FORMAT_VERSION: C.ARCHIVE_FORMAT_VERSION,

    HARNESS_DB_NAME: C.HARNESS_DB_NAME,
    HARNESS_DB_VERSION: C.HARNESS_DB_VERSION,
    PRODUCT_DB_NAME: C.PRODUCT_DB_NAME,
    PRODUCT_DB_VERSION: C.PRODUCT_DB_VERSION,

    SHIRT_NUMBER_MIN: C.SHIRT_NUMBER_MIN,
    SHIRT_NUMBER_MAX: C.SHIRT_NUMBER_MAX,

    RECORDER_STOP_TIMEOUT_MS: C.RECORDER_STOP_TIMEOUT_MS,

    SET_PIECE_MARKER_PT: C.SET_PIECE_MARKER_PT,
    SET_PIECE_DIAGRAM_ASPECT: C.SET_PIECE_DIAGRAM_ASPECT,

    CARD_WIDTH_PX: C.CARD_WIDTH_PX,
    CARD_TALK_POINT_PX: C.CARD_TALK_POINT_PX,
    CARD_MESSAGE_WIDTH_PT: C.CARD_MESSAGE_WIDTH_PT,
    CARD_MIN_LEGIBLE_PT: C.CARD_MIN_LEGIBLE_PT,

    LOG_FLUSH_IDLE_MS: C.LOG_FLUSH_IDLE_MS,

    COACH_TIMEOUT_MS: C.COACH_TIMEOUT_MS,
    COACH_PREFLIGHT_TIMEOUT_MS: C.COACH_PREFLIGHT_TIMEOUT_MS,
    COACH_MAX_SUGGESTIONS: C.COACH_MAX_SUGGESTIONS,
    COACH_ADJUSTMENT_MAX_CHARS: C.COACH_ADJUSTMENT_MAX_CHARS,
    COACH_CARD_MAX_ENTRIES: C.COACH_CARD_MAX_ENTRIES,
    COACH_REQUEST_MAX_KB: C.COACH_REQUEST_MAX_KB,

    LOCK_NUDGE_MIN_MIN: C.LOCK_NUDGE_MIN_MIN,
    LOCK_NUDGE_MIN_MAX: C.LOCK_NUDGE_MIN_MAX,
    LOCK_NUDGE_STEP_MIN: C.LOCK_NUDGE_STEP_MIN,
  }),
);
